import React from "react";
import { OidcIdentity } from "@rsmus/react-auth";
import { RecoilRoot } from "recoil";
import App from "./App";
import SignOut from "./pages/signOut/SignOut";
import SiteNotification from "./pages/SiteNotification/SiteNotification";
import { envProperties } from "./util/EnvironmentProperties";

type RootView = "app" | "signout" | "sitenotification";

const PENDING_ROOT_VIEW_KEY = "pendingRootView";

const normalizePathname = (pathname: string): string => {
  const normalizedPath = pathname.trim().toLowerCase().replace(/\/+$/, "");
  return normalizedPath || "/";
};

const resolveViewFromPath = (pathname: string): RootView => {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === "/signout") return "signout";
  if (normalizedPath === "/sitenotification") return "sitenotification";

  return "app";
};

const isOidcCallbackRequest = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has("code") && searchParams.has("state");
};

const resolveInitialView = (): RootView => {
  const currentView = resolveViewFromPath(window.location.pathname);
  const oidcCallbackRequest = isOidcCallbackRequest();

  if (!oidcCallbackRequest && currentView !== "app") {
    sessionStorage.setItem(PENDING_ROOT_VIEW_KEY, currentView);
    return currentView;
  }

  if (oidcCallbackRequest) {
    const pendingView = sessionStorage.getItem(PENDING_ROOT_VIEW_KEY);
    if (pendingView === "sitenotification" || pendingView === "signout") {
      return pendingView;
    }
  }

  return currentView;
};

const initialView = resolveInitialView();

const handleDeepLinkRedirect = (url: string): void => {
  const pendingView = sessionStorage.getItem(PENDING_ROOT_VIEW_KEY);

  if (pendingView === "sitenotification") {
    window.history.replaceState({}, "", "/siteNotification");
  } else if (pendingView === "signout") {
    window.history.replaceState({}, "", "/signout");
  } else if (url) {
    try {
      const restoredUrl = new URL(url, window.location.origin);
      window.history.replaceState(
        {},
        "",
        `${restoredUrl.pathname}${restoredUrl.search}${restoredUrl.hash}`,
      );
    } catch (error) {
      console.error("[OIDC] Could not restore deep-link URL", error);
    }
  }

  sessionStorage.removeItem(PENDING_ROOT_VIEW_KEY);
};

const RootContent = (): React.ReactElement => {
    console.log("Initial view:", initialView);
  if (initialView === "signout") return <SignOut />;
  if (initialView === "sitenotification") return <SiteNotification />;

  
  return (
    <RecoilRoot>
      <App />
    </RecoilRoot>
  );
};

const ApplicationRoot = (): React.ReactElement => (
  <OidcIdentity
    authority={envProperties.authentication.authority}
    redirect_uri={window.location.origin}
    client_id={envProperties.authentication.clientId}
    scope={envProperties.authentication.scope}
    postLogoutRedirectUri="rsmus.com"
    automaticSilentRenew={true}
    autoLogin={true}
    redirectAfterLogout={true}
    accessTokenExpiringNotificationTimeInSeconds={180}
    onDeepLinkRedirectCallback={handleDeepLinkRedirect}
  >
    <RootContent />
  </OidcIdentity>
);

export default ApplicationRoot;